import React from "react";

import {
  columnChoiceLine,
  columnMapRows,
  type SheetsHealth,
} from "../lib/sheets-health";
import { Modal } from "./Modal";
import { Button } from "./ui/Button";

type SheetsColumnMapDialogProps = {
  health: SheetsHealth;
  /** The action id that is in flight, so its button shows the spinner. */
  busy: string | null;
  onChoose: (choice: "map" | "keep_both") => void;
  onClose: () => void;
};

/**
 * MOSA-409 case 4. The owner decides what a renamed column means.
 *
 * The dialog shows the rename rather than asking the owner to remember it:
 * "Email" on the left, "E-mail" on the right, per column. Both choices are
 * spelled out, because the difference is not obvious from the labels and it is
 * not reversible by guessing.
 *
 * Until the owner answers, the column stays read-only. The API enforces that;
 * this dialog is the way out of it.
 */
export const SheetsColumnMapDialog: React.FC<SheetsColumnMapDialogProps> = ({
  health,
  busy,
  onChoose,
  onClose,
}) => {
  const rows = columnMapRows(health);

  return (
    <Modal
      open
      onClose={onClose}
      maxWidth="max-w-lg"
      title="Map the renamed columns"
      subtitle={
        health.tab ? `Tab "${health.tab}"` : "The header row of this sheet"
      }
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="md"
            loading={busy === "keep_both"}
            onClick={() => onChoose("keep_both")}
          >
            Keep both
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={busy === "map"}
            onClick={() => onChoose("map")}
          >
            Map
          </Button>
        </div>
      }
    >
      <div data-testid="column-map-dialog" className="flex flex-col gap-4">
        {rows.length > 0 ? (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {rows.map((row) => (
              <li
                key={`${row.from}->${row.to}`}
                className="flex flex-wrap items-center gap-2 rounded-md border border-od-border bg-od-surface-2 px-3 py-2 text-sm"
              >
                <span className="font-medium text-od-muted line-through">
                  {row.from}
                </span>
                <span aria-hidden="true" className="text-od-muted">
                  →
                </span>
                <span className="font-semibold text-od-text">{row.to}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 text-sm text-od-muted">
            The header row changed. Choose how to carry the panel's fields over.
          </p>
        )}
        <dl className="m-0 flex flex-col gap-3">
          <div>
            <dt className="m-0 text-sm font-semibold text-od-text">Map</dt>
            <dd className="m-0 text-xs text-od-muted">
              {columnChoiceLine("map")}
            </dd>
          </div>
          <div>
            <dt className="m-0 text-sm font-semibold text-od-text">
              Keep both
            </dt>
            <dd className="m-0 text-xs text-od-muted">
              {columnChoiceLine("keep_both")}
            </dd>
          </div>
        </dl>
      </div>
    </Modal>
  );
};

export default SheetsColumnMapDialog;
